/**
 * Page listing all quotes pending the current user's approval.
 */
import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { HourglassEmpty } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { DataTable } from '../../components/tables/DataTable';
import { ROUTES } from '../../constants/routes';
import { getPendingApprovals } from '../../services/quoteService';
import { QuotesSideRail } from './QuotesSideRail';
import type { PendingApproval } from '../../types/approval';

const ROLE_LABELS: Record<string, string> = {
  reviewer: 'Reviewer+',
  technician: 'Technician+',
  admin: 'Admin Only',
};

const columns = [
  { field: 'QuoteNumber', headerName: 'Quote #', width: 140 },
  { field: 'Title', headerName: 'Title', width: 220 },
  { field: 'ClientName', headerName: 'Client', width: 200 },
  { field: 'GrandTotalFormatted', headerName: 'Total', width: 140, align: 'right' as const },
  { field: 'RequiredRoleLabel', headerName: 'Required Role', width: 140 },
  { field: 'SubmittedDate', headerName: 'Submitted', width: 140 },
];

export const PendingApprovalsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getPendingApprovals());
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRowClick = (row: Record<string, unknown>) => {
    const quoteKey = row.QuoteKey as string;
    navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', quoteKey));
  };

  const tableData = items.map((item) => ({
    ...item,
    GrandTotalFormatted: item.GrandTotal.toLocaleString('en-CA', {
      style: 'currency', currency: item.Currency || 'CAD',
    }),
    RequiredRoleLabel: ROLE_LABELS[item.RequiredRole] || item.RequiredRole,
    SubmittedDate: item.DecidedAt
      ? new Date(item.DecidedAt).toLocaleDateString('en-CA')
      : '',
  }));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <QuotesSideRail active="pending-approvals" />
      <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, pt: 1, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Breadcrumb items={[
              { label: 'Quotes', path: ROUTES.QUOTES },
              { label: 'Pending Approvals' },
            ]} />

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
                fontSize: '1.75rem', lineHeight: 1.2, mt: 2, mb: 3,
              }}
            >
              Pending Approvals
            </Typography>

            {items.length === 0 ? (
              <Box sx={{
                textAlign: 'center', py: 8,
                border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                borderRadius: 2,
              }}>
                <HourglassEmpty sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No quotes awaiting your approval</Typography>
              </Box>
            ) : (
              <DataTable
                columns={columns}
                data={tableData}
                loading={false}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PendingApprovalsPage;
